/**
 * PsbStage6aPage — Route B Closing Stock Import
 *
 * Step 1: Source   — select an applied Stage 5 session (S0→S1→S2→S5 chain)
 * Step 2: Bridge   — dry-run plan: product mapping, CLOS/CLOSCOST totals,
 *                    reconciliation against £4,054.063 expected total
 * Step 3: Review   — per-product closing rows (importable vs held tabs)
 * Step 4: Apply    — write count_entries for matched products; show provenance ledger
 *
 * Semantics enforced at this layer:
 *  - Zero CLOS (explicit 0.000000) is displayed as "0" — distinct from blank/null
 *  - Stocktake precedence: "skipped_existing_entry" shown in yellow
 *  - Production gate: site 271 / Preview only (enforced in backend)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, ChevronRight, ClipboardList, Database,
  Loader2, ShieldAlert, AlertTriangle, Package, Scale,
  BadgeCheck, Layers,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../services/api';

// ── Constants ────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Source' },
  { id: 2, label: 'Bridge' },
  { id: 3, label: 'Review' },
  { id: 4, label: 'Apply' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtGBP(v) {
  if (v == null || v === '' || v === 'None') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  const sign = n < 0 ? '−' : '';
  return `${sign}£${Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

function fmtQty(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return n.toFixed(4);
}

function fmtDate(s) {
  if (!s) return '—';
  return s;
}

// ── Step nav ─────────────────────────────────────────────────────────────────

function StepNav({ current }) {
  return (
    <ol className="flex items-center gap-2 text-sm mb-6" data-testid="step-nav-s6a">
      {STEPS.map((s, idx) => {
        const done = s.id < current;
        const active = s.id === current;
        return (
          <React.Fragment key={s.id}>
            <li
              data-testid={`step-nav-s6a-${s.id}`}
              className={`flex items-center gap-1.5 font-medium ${active ? 'text-slate-900' : done ? 'text-emerald-600' : 'text-slate-400'}`}
            >
              {done
                ? <CheckCircle2 size={15} className="text-emerald-500" />
                : <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs border ${active ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-300 text-slate-400'}`}>{s.id}</span>
              }
              {s.label}
            </li>
            {idx < STEPS.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

// ── ReconciliationBadge ───────────────────────────────────────────────────────

function ReconciliationBadge({ ok, label }) {
  return ok
    ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200"><CheckCircle2 size={12} />{label || 'Reconciled'}</span>
    : <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200"><AlertTriangle size={12} />{label || 'Mismatch'}</span>;
}

// ── BridgeCard ────────────────────────────────────────────────────────────────

function BridgeCard({ bridge }) {
  if (!bridge) return null;
  const b = bridge;
  return (
    <div className="space-y-4">
      {/* Grand total check */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Grand Total Reconciliation</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Stat label="Source rows" value={b.source_rows} mono />
          <Stat label="Non-zero CLOS" value={b.nonzero_clos_rows} mono />
          <Stat label="Zero CLOS (valid)" value={b.zero_clos_rows} mono />
          <Stat label="Total CLOSCOST" value={fmtGBP(b.source_closcost)} mono />
          <Stat label="Expected (PDF)" value="£4,054.063" mono />
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Matches PDF</span>
            <ReconciliationBadge ok={b.total_matches_expected} label={b.total_matches_expected ? '£4,054.063 ✓' : 'Mismatch'} />
          </div>
        </div>
      </div>

      {/* Product mapping split */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Product Mapping</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Stat label="Importable products" value={b.importable_count} mono highlight="green" />
          <Stat label="Held (no S1 mapping)" value={b.held_count} mono highlight="amber" />
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Rows reconcile</span>
            <ReconciliationBadge ok={b.reconciles} />
          </div>
          <Stat label="Importable CLOSCOST" value={fmtGBP(b.importable_closcost)} mono />
          <Stat label="Held CLOSCOST" value={fmtGBP(b.held_closcost)} mono />
        </div>
        {b.held_count > 0 && (
          <p className="mt-3 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
            {b.held_count} product{b.held_count !== 1 ? 's' : ''} held — no approved Stage 1 mapping. These closing quantities cannot be posted until Stage 1 mappings are approved.
          </p>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, mono, highlight }) {
  const colour = highlight === 'green' ? 'text-emerald-700' : highlight === 'amber' ? 'text-amber-700' : 'text-slate-900';
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-sm font-semibold ${colour} ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

// ── RowTable ──────────────────────────────────────────────────────────────────

function RowTable({ rows, loading }) {
  if (loading) return <div className="flex items-center gap-2 text-slate-500 text-sm py-4"><Loader2 size={16} className="animate-spin" />Loading rows…</div>;
  if (!rows || rows.length === 0) return <p className="text-sm text-slate-400 py-4">No rows in this category.</p>;

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-xs border-collapse" data-testid="s6a-row-table">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Itemcode</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Product</th>
            <th className="text-right px-3 py-2 font-semibold text-slate-600">CLOS qty</th>
            <th className="text-right px-3 py-2 font-semibold text-slate-600">CLOSCOST</th>
            <th className="text-right px-3 py-2 font-semibold text-slate-600">WAC</th>
            <th className="text-right px-3 py-2 font-semibold text-slate-600">OPEN qty</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
              <td className="px-3 py-1.5 font-mono text-slate-700">{r.itemcode}</td>
              <td className="px-3 py-1.5 text-slate-600 max-w-[180px] truncate" title={r.bp_name_snapshot || r.hold_reason}>{r.bp_name_snapshot || <span className="text-slate-400 italic">{r.hold_reason}</span>}</td>
              <td className="px-3 py-1.5 text-right font-mono">{fmtQty(r.clos_qty)}</td>
              <td className="px-3 py-1.5 text-right font-mono">{fmtGBP(r.closcost)}</td>
              <td className="px-3 py-1.5 text-right font-mono text-slate-500">{r.wac ? fmtQty(r.wac) : '—'}</td>
              <td className="px-3 py-1.5 text-right font-mono text-slate-500">{fmtQty(r.open_qty)}</td>
              <td className="px-3 py-1.5">
                {r.resolution_status === 'importable'
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Importable</Badge>
                  : <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]" title={r.hold_reason}>Held</Badge>
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── LinkTable ─────────────────────────────────────────────────────────────────

function LinkTable({ links }) {
  if (!links || links.length === 0) return <p className="text-sm text-slate-400">No provenance links recorded.</p>;

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-xs border-collapse" data-testid="s6a-link-table">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">bp_id</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Line ID</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Entry ID</th>
            <th className="text-right px-3 py-2 font-semibold text-slate-600">CLOS qty</th>
            <th className="text-right px-3 py-2 font-semibold text-slate-600">CLOSCOST</th>
            <th className="text-right px-3 py-2 font-semibold text-slate-600">WAC</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Precedence</th>
          </tr>
        </thead>
        <tbody>
          {links.map((lk, i) => (
            <tr key={lk.id || i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
              <td className="px-3 py-1.5 font-mono">{lk.beverage_product_id}</td>
              <td className="px-3 py-1.5 font-mono text-slate-500">{lk.count_sheet_line_id}</td>
              <td className="px-3 py-1.5 font-mono text-slate-500">{lk.count_entry_id}</td>
              <td className="px-3 py-1.5 text-right font-mono">{fmtQty(lk.clos_qty)}</td>
              <td className="px-3 py-1.5 text-right font-mono">{fmtGBP(lk.closcost)}</td>
              <td className="px-3 py-1.5 text-right font-mono text-slate-500">{lk.wac ? fmtQty(lk.wac) : '—'}</td>
              <td className="px-3 py-1.5">
                {lk.precedence_result === 'written'
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Written</Badge>
                  : lk.precedence_result === 'skipped_existing_entry'
                    ? <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Stocktake wins</Badge>
                    : <Badge className="bg-slate-100 text-slate-600 text-[10px]">{lk.precedence_result}</Badge>
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PsbStage6aPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 1 — source sessions
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);

  // Step 2 — plan / bridge
  const [plan, setPlan] = useState(null);
  const [rowCounts, setRowCounts] = useState({});

  // Step 3 — rows
  const [reviewTab, setReviewTab] = useState('importable');
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [rowPage, setRowPage] = useState(1);
  const ROW_PAGE_SIZE = 100;

  // Step 4 — apply result
  const [applyResult, setApplyResult] = useState(null);
  const [applying, setApplying] = useState(false);

  // URL persistence
  const planIdParam = searchParams.get('planId');

  // ── Load from URL ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!planIdParam) return;
    (async () => {
      try {
        const d = await api.get(`/admin/psb-stage6a/plans/${planIdParam}`);
        setPlan(d.data.plan);
        setRowCounts(d.data.row_counts || {});
        if (d.data.plan.status === 'applied') {
          setStep(4);
        } else {
          setStep(3);
        }
      } catch {
        /* ignore — plan not found */
      }
    })();
  }, [planIdParam]);

  // ── Step 1: load source sessions ────────────────────────────────────────────
  useEffect(() => {
    if (step !== 1) return;
    (async () => {
      setLoading(true);
      try {
        const d = await api.get('/admin/psb-stage6a/s5-sessions');
        setSessions(d.data.sessions || []);
      } catch (e) {
        toast.error('Failed to load Stage 5 sessions');
      } finally {
        setLoading(false);
      }
    })();
  }, [step]);

  // ── Step 3: load rows on tab/page change ─────────────────────────────────────
  useEffect(() => {
    if (step !== 3 || !plan) return;
    (async () => {
      setRowsLoading(true);
      try {
        const d = await api.get(
          `/admin/psb-stage6a/plans/${plan.run_id}?category=${reviewTab}&page=${rowPage}&page_size=${ROW_PAGE_SIZE}`
        );
        setRows(d.data.rows || []);
        setTotalRows(d.data.total || 0);
        setRowCounts(d.data.row_counts || rowCounts);
      } catch (e) {
        toast.error('Failed to load rows');
      } finally {
        setRowsLoading(false);
      }
    })();
  }, [step, plan, reviewTab, rowPage]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleCreatePlan = useCallback(async () => {
    if (!selectedSession) return;
    setLoading(true);
    try {
      const d = await api.post('/admin/psb-stage6a/plans', {
        stage0_run_id: selectedSession.stage0_run_id,
        s1_run_id: selectedSession.s1_run_id,
        s2_plan_id: selectedSession.s2_plan_id,
        s5_run_id: selectedSession.s5_run_id,
        target_session_id: selectedSession.target_session_id,
        site_id: selectedSession.target_site_id,
      });
      setPlan(d.data.plan);
      setRowCounts(d.data.row_counts || {});
      if (d.data.idempotent) toast.info('Existing dry-run plan loaded');
      setSearchParams({ planId: d.data.plan.run_id });
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create plan');
    } finally {
      setLoading(false);
    }
  }, [selectedSession, setSearchParams]);

  const handleApply = useCallback(async () => {
    if (!plan) return;
    setApplying(true);
    try {
      const d = await api.post(`/admin/psb-stage6a/plans/${plan.run_id}/apply`);
      setApplyResult(d.data);
      setPlan(prev => prev ? { ...prev, status: 'applied' } : prev);
      setStep(4);
      toast.success(`Stage 6A applied — ${d.data.written} entr${d.data.written !== 1 ? 'ies' : 'y'} written`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  }, [plan]);

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
          data-testid="back-btn"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Scale size={20} className="text-indigo-600" />
            PSB Stage 6A — Closing Stock Import
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Import TRANFILE Ref 067 CLOS/CLOSCOST into session count_entries · Preview only · Site 271
          </p>
        </div>
      </div>

      <StepNav current={step} />

      {/* Step 1 — Source */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Database size={16} className="text-indigo-500" />Select Source Session</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Select an applied Stage 5 session as the closing stock source. The stage0→S1→S2→S5 fingerprint chain will be validated automatically.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2">
              <ShieldAlert size={15} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700">Preview only — writes are gated to site 271. Production writes remain disabled.</p>
            </div>

            {loading && <div className="flex items-center gap-2 text-slate-400 text-sm py-2"><Loader2 size={16} className="animate-spin" />Loading sessions…</div>}

            {!loading && sessions.length === 0 && (
              <p className="text-sm text-slate-400">No applied Stage 5 sessions found for allowed sites.</p>
            )}

            {!loading && sessions.map(s => (
              <div
                key={s.s5_run_id}
                onClick={() => setSelectedSession(s)}
                className={`cursor-pointer rounded-xl border p-4 transition-all ${selectedSession?.s5_run_id === s.s5_run_id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                data-testid={`session-card-${s.target_session_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{s.session_reference}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Session {s.target_session_id} · Site {s.target_site_id} · {s.period_from} → {s.period_to}</p>
                    <p className="text-xs text-slate-400 mt-0.5 font-mono">S5: {s.s5_run_id.slice(0, 8)}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">S5 Applied</Badge>
                    <Badge className="bg-slate-100 text-slate-600 text-[10px]">{s.session_status}</Badge>
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleCreatePlan}
                disabled={!selectedSession || loading}
                data-testid="create-plan-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {loading ? <><Loader2 size={15} className="animate-spin mr-1" />Building plan…</> : <>Run dry-run <ChevronRight size={15} className="ml-1" /></>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2 — Bridge */}
      {step === 2 && plan && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><Layers size={16} className="text-indigo-500" />Closing Stock Bridge</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Plan:</span>
              <code className="bg-slate-100 rounded px-1.5 py-0.5 font-mono">{plan.run_id.slice(0, 8)}</code>
              <span>→</span>
              <span>Session {plan.target_session_id}</span>
              <span>·</span>
              <span>Site {plan.target_site_id}</span>
            </div>

            <BridgeCard bridge={plan.bridge} />

            <div className="flex gap-3 justify-between pt-2">
              <Button variant="outline" onClick={() => { setStep(1); setPlan(null); setSearchParams({}); }} data-testid="back-to-source-btn">
                <ArrowLeft size={15} className="mr-1" />Back
              </Button>
              <Button onClick={() => { setStep(3); setReviewTab('importable'); setRowPage(1); }} data-testid="proceed-to-review-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                Review rows <ChevronRight size={15} className="ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3 — Review */}
      {step === 3 && plan && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><ClipboardList size={16} className="text-indigo-500" />Review Closing Rows</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Tab bar */}
            <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
              {[
                { key: 'importable', label: `Importable (${rowCounts.importable ?? '…'})`, colour: 'emerald' },
                { key: 'held', label: `Held (${rowCounts.held ?? '…'})`, colour: 'amber' },
              ].map(t => (
                <button
                  key={t.key}
                  onClick={() => { setReviewTab(t.key); setRowPage(1); }}
                  data-testid={`tab-${t.key}`}
                  className={`text-xs font-medium px-3 py-1.5 rounded-md transition-all ${reviewTab === t.key ? `bg-${t.colour}-50 text-${t.colour}-700 border border-${t.colour}-200` : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <RowTable rows={rows} loading={rowsLoading} />

            {/* Pagination */}
            {totalRows > ROW_PAGE_SIZE && (
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Showing {Math.min((rowPage - 1) * ROW_PAGE_SIZE + 1, totalRows)}–{Math.min(rowPage * ROW_PAGE_SIZE, totalRows)} of {totalRows}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={rowPage === 1} onClick={() => setRowPage(p => p - 1)}>Prev</Button>
                  <Button variant="outline" size="sm" disabled={rowPage * ROW_PAGE_SIZE >= totalRows} onClick={() => setRowPage(p => p + 1)}>Next</Button>
                </div>
              </div>
            )}

            <div className="flex gap-3 justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="back-to-bridge-btn">
                <ArrowLeft size={15} className="mr-1" />Back
              </Button>
              {plan.status === 'applied' ? (
                <Button onClick={() => setStep(4)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="view-apply-result-btn">
                  <CheckCircle2 size={15} className="mr-1" />View result
                </Button>
              ) : (
                <Button
                  onClick={handleApply}
                  disabled={applying || (rowCounts.importable || 0) === 0}
                  data-testid="apply-btn"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {applying ? <><Loader2 size={15} className="animate-spin mr-1" />Applying…</> : <>Apply Stage 6A <ChevronRight size={15} className="ml-1" /></>}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4 — Apply result */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BadgeCheck size={16} className="text-emerald-500" />Stage 6A Applied
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Summary */}
            {applyResult && (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 space-y-3">
                <p className="text-sm font-semibold text-emerald-800">Closing stock written to session {applyResult.target_session_id}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Stat label="Entries written" value={applyResult.written} highlight="green" />
                  <Stat label="Stocktake wins (skipped)" value={applyResult.skipped_existing} />
                  <Stat label="Written CLOSCOST" value={fmtGBP(applyResult.written_closcost)} mono highlight="green" />
                </div>
                {applyResult.idempotent && (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
                    Idempotent — plan was already applied. No new rows written.
                  </p>
                )}
              </div>
            )}

            {/* Bridge recap */}
            {plan?.bridge && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Bridge Recap</p>
                <BridgeCard bridge={plan.bridge} />
              </div>
            )}

            {/* Provenance links */}
            {applyResult?.links && applyResult.links.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Provenance Ledger</p>
                <LinkTable links={applyResult.links} />
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setStep(3)} data-testid="back-to-review-btn">
                <ArrowLeft size={15} className="mr-1" />Review
              </Button>
              <Button variant="outline" onClick={() => navigate('/stocktaking/sessions')} data-testid="view-session-btn">
                <Package size={15} className="mr-1" />View session
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
